import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect }  from 'react-redux';
import './RoomSettingsModal.css';

import * as roomsActions from '../actions/rooms-actions.js';

import UserAvatar from './UserAvatar.js';
import PopoverList from './PopoverList.js';
import PopoverListItem from './PopoverListItem.js';

class RoomSettingsModal extends Component {
  constructor(props) {
    super(props);

    const { room } = props;
    const { name } = room;

    this.state = {
      roomName: name
    };
  }

  /**
   * Save the room changes (room name) and update the originalRoomName
   * state field.
   *
   * @param {SyntheticEvent} ev The click event of the save button.
   * @memberof RoomSettingsModal
   */
  saveChanges = (ev) => {
    ev.preventDefault();
    const { room } = this.props;
    const { roomName } = this.state;

    this.props.roomsActions.updateRoom(room.id, {
      name: roomName
    });
  }

  /**
   * Cancel the room changes (room name) and update the state of the form
   * back to the value of the original room name.
   *
   * @memberof RoomSettingsModal
   */
  cancelChanges = (ev) => {
    ev.preventDefault();
    const { room } = this.props;
    const { name } = room;

    this.setState({
      roomName: name
    });
  }

  /**
   * Close the modal if there are no unsaved changes. Triggered by clicking the overlay
   * behind the modal.
   *
   * @memberof RoomSettingsModal
   */
  close = () => {
    const { state } = this;

    // Don't close the modal if the user has unsaved changes.
    if (this.hasUnsavedChanges(state)) {
      return;
    }

    // Otherwise, close the modal.
    this.props.close();
  }

  /**
   * Delete (or leave) the room. Triggered by clicking the "Delete room" or "Leave room" button
   * depending on if you're an owner of the room or not.
   *
   * @memberof RoomSettingsModal
   */
  deleteRoom = () => {
    const { currentUser, room, close } = this.props;
    let userIsOwner = currentUser.id === room.owner.id;

    // If the user is just a participant of the room, show the leave message.
    let confirmMessage = `Are you sure you want to leave the room ${room.name}? You'll need to be re-invited if you wish to join it later.`;

    // If the user is the owner, show the delete message.
    if (userIsOwner) {
      confirmMessage = `Are you sure you want to delete the room ${room.name} for you and all participants? This can't be undone!`;
    }

    let confirmDelete = confirm(confirmMessage);
    if (confirmDelete) {
      this.props.roomsActions.deleteRoom(room.id);
      close();
    }
  }

  /**
   * Invite others to join the room. Closes the modal and opens the invite flow
   * for the room on the welcome page.
   *
   * @memberof RoomSettingsModal
   */
  inviteUsers = (ev) => {
    ev.preventDefault();
    const { room } = this.props;
    const { id } = room;

    // TODO: Close the modal and open the invite flow in the welcome page.
    this.close();
    this.props.openInviteFlow(id);
  }

  /**
   * Handle input changes in the room name field. Updates the `state.roomName` property.
   *
   * @memberof RoomSettingsModal
   */
  handleInputChange = (ev) => {
    const { target } = ev;
    const { value } = target;

    this.setState({
      roomName: value
    });
  }

  /**
   * Helper function to determine if the form has any unsaved changes. Returns a boolean
   * value, and takes in the current state of the component.
   *
   * @param {Object} state The current state of the component.
   * @param {String} state.roomName The entered room name value in the form.
   * @param {String} state.originalRoomName The room name currently persisted to the service.
   * @returns Boolean
   * @memberof RoomSettingsModal
   */
  hasUnsavedChanges = (state) => {
    const { room } = this.props;
    const { roomName } = state;

    if (roomName.trim() !== room.name.trim()) {
      return true;
    }

    return false;
  }

  /**
   * Render a member cell for the given user. Used in the members list.
   *
   * @param {Object} user The user to render.
   * @returns JSX
   * @memberof RoomSettingsModal
   */
  renderMemberCell(user) {
    const { currentUser, room } = this.props;
    const { displayName, id } = user;

    const { owner } = room;
    const currentUserIsOwner = currentUser.id === owner.id;
    const renderedUserIsOwner = user.id === owner.id;

    let removeButton;

    // If the current user is allowed to remove the `user` from the room,
    // render the `Remove` button.
    //
    // Only allow the user to be removed if the current user is the owner of the room,
    // and if the rendered user is not.
    if (currentUserIsOwner && !renderedUserIsOwner) {
      removeButton = (
        <div className="RoomMembersCell__actions">
          <button className="ClearButton ClearButton--destroy" onClick={() => { this._removeUser(room, user) }}>Remove</button>
        </div>
      );
    }

    return (
      <div className="RoomMembersCell" key={id}>
        <div className="RoomMembersCell__avatar">
          <UserAvatar user={user} />
        </div>

        <div className="RoomMembersCell__name">{displayName}</div>

        {removeButton}
      </div>
    );
  }

  /**
   * Remove the user from the given room.
   * @param {Object} room The room to remove the user from.
   * @param {String} user The user to remove.
   */
  _removeUser = (room, user) => {
    // Confirm they really want to remove this user from the room.
    let message = `Are you sure you want to remove ${user.displayName} from the ${room.name} room? This action can't be undone!`;
    if (!confirm(message)) {
      return;
    }

    this.props.roomsActions.removeUserFromRoom(room.id, [ user.id ]);
  }

  /**
   * Render out the settings modal.
   *
   * @returns
   * @memberof RoomSettingsModal
   */
  render() {
    const { roomName } = this.state;
    const { room } = this.props;
    const { name, members } = room;

    let userIsOwner = this.props.session.user.id === room.owner.id;

    // Disable the form and all its input elements if the current user isn't the owner of the room.
    let formDisabled = !userIsOwner;

    // Assemble the list of member cells.
    let memberCells = members.map((user) => this.renderMemberCell(user));

    // Default left header button to empty, right header button to close.
    let leftButton;
    let rightButton = <a href="#" onClick={this.props.close} className="RoomSettingsModal__header-close"><svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M2.18 0L0 2.18l4.54 4.54L0 11.26l2.18 2.18L6.72 8.9l4.56 4.56 2.18-2.18L8.9 6.72l4.56-4.54L11.28 0 6.72 4.54z" fill="#000000" fillRule="evenodd"/></svg></a>;

    // If we have unsaved changes, override the header buttons with Cancel and Save buttons.
    if (this.hasUnsavedChanges(this.state)) {
      leftButton = <a className="RoomSettingsModal__header-link" onClick={this.cancelChanges}>Cancel</a>;

      rightButton = <a className="RoomSettingsModal__header-link RoomSettingsModal__header-link--primary" onClick={this.saveChanges}>Save</a>;
    }

    // Invite button (optionally added if the user is the room owner).
    let inviteButton;
    if (userIsOwner) {
      inviteButton = <a onClick={this.inviteUsers} className="form-secondary-action">Invite others</a>;
    }

    // Assemble the delete/leave button.
    let leaveSection = (<section className="RoomSettingsModal__section">
        <label className="form-label">Leave room</label>
        <p className="form-label-text">Leaving a room removes it from your rooms list. To rejoin, you'll need to be reinvited.</p>
        <PopoverList>
          <PopoverListItem destroyStyle={true} onClick={this.deleteRoom}><svg width="12" height="14" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M7.05 2.726l-.912-.976H4.946l-.913.976h-1.24S4.152 1.108 4.333.888c.139-.168.28-.232.464-.232h1.49c.183 0 .325.064.464.232.18.22 1.54 1.838 1.54 1.838H7.05zm.8-1.624L7.225.407C6.983.064 6.721 0 6.21 0H4.874c-.511 0-.773.064-1.015.407l-.626.695C1.359 1.43 0 2.298 0 2.938v.124C0 4.19 2.481 5.104 5.542 5.104c3.06 0 5.541-.914 5.541-2.042v-.124c0-.64-1.358-1.508-3.232-1.836zM.72 5.477l.804 7.08c.043.336 1.667 1.441 4.018 1.443 2.35-.002 3.974-1.107 4.018-1.443l.803-7.08C9.135 6.164 7.3 6.49 5.542 6.49c-1.758 0-3.594-.326-4.821-1.013z" fill="#FF3F3F" fillRule="evenodd"/></svg> Leave room</PopoverListItem>
        </PopoverList>
      </section>);

    // If the user is the owner of the room, change it to the delete section.
    if (userIsOwner) {
      leaveSection = (<section className="RoomSettingsModal__section">
        <label className="form-label">Delete room</label>
        <p className="form-label-text">Deleting a room in Normal Chat will permanently delete it and its contents. We can’t undo this!</p>
        <PopoverList>
          <PopoverListItem destroyStyle={true} onClick={this.deleteRoom}><svg width="12" height="14" viewBox="0 0 12 14" xmlns="http://www.w3.org/2000/svg"><path d="M7.05 2.726l-.912-.976H4.946l-.913.976h-1.24S4.152 1.108 4.333.888c.139-.168.28-.232.464-.232h1.49c.183 0 .325.064.464.232.18.22 1.54 1.838 1.54 1.838H7.05zm.8-1.624L7.225.407C6.983.064 6.721 0 6.21 0H4.874c-.511 0-.773.064-1.015.407l-.626.695C1.359 1.43 0 2.298 0 2.938v.124C0 4.19 2.481 5.104 5.542 5.104c3.06 0 5.541-.914 5.541-2.042v-.124c0-.64-1.358-1.508-3.232-1.836zM.72 5.477l.804 7.08c.043.336 1.667 1.441 4.018 1.443 2.35-.002 3.974-1.107 4.018-1.443l.803-7.08C9.135 6.164 7.3 6.49 5.542 6.49c-1.758 0-3.594-.326-4.821-1.013z" fill="#FF3F3F" fillRule="evenodd"/></svg> Delete room</PopoverListItem>
        </PopoverList>
      </section>)
    }

    // Finally, return the assembled markup.
    return (
      <div className="RoomSettingsModal__wrapper">
        <div className="RoomSettingsModal__overlay" onClick={this.close}></div>
        <div className="RoomSettingsModal">
          <header className="RoomSettingsModal__header">
            <section className="RoomSettingsModal__header-left">{leftButton}</section>
            <h1>{name}</h1>
            <section className="RoomSettingsModal__header-right">{rightButton}</section>
          </header>

          <section className="RoomSettingsModal__content">
            <section className="RoomSettingsModal__section">
              <label htmlFor="name" className="form-label">Room Name</label>
              <input disabled={formDisabled} type="text" onChange={this.handleInputChange} name="name" className="form-input form-input--fullwidth" placeholder="Room name" value={roomName} />
            </section>

            <section className="RoomSettingsModal__section">
              <label className="form-label">Room Members</label>

              {inviteButton}

              <div className="RoomMembersList">
                {memberCells}
              </div>
            </section>

            {leaveSection}
          </section>
        </div>
      </div>
    );
  }
};

// Injecting the Redux session state here as the component needs to be able
// to see if the current user is the owner of the room or not.
const mapStateToProps = (state) => {
  return {
    session: state.session
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    roomsActions: bindActionCreators(roomsActions, dispatch)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RoomSettingsModal);



// WEBPACK FOOTER //
// ./src/components/RoomSettingsModal.js